import { useCallback, useEffect, useState } from 'react'
import { FileText, Loader2 } from 'lucide-react'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { FileUpload } from '@/components/ui/FileUpload'
import { SpecialtyTestTable } from './SpecialtyTestTable'
import { useAuthority } from '@/hooks/useAuthority'
import {
  CredentialingProvidersService,
  type CredentialingProvider, type ProviderContractItem,
} from '@/services/modules/CredentialingService'
import { toast } from '@/components/ui/Toast'
import { dateBR } from '@/lib/format'

export type ProviderViewMode = 'specialties' | 'tests' | 'bank' | 'contract'

/**
 * Modal de visualização de especialidades / exames / dados bancários / contrato
 * de um credenciado. Compartilhada entre o Kanban e a listagem de credenciados.
 */
export function ProviderViewModal({ providerId, providerName, mode, onClose, onChanged }: {
  providerId: string
  providerName: string
  mode: ProviderViewMode
  onClose: () => void
  /** Disparado após qualquer alteração (contrato, especialidade, exame). */
  onChanged?: () => void
}) {
  const [loading, setLoading] = useState(true)
  const [provider, setProvider] = useState<CredentialingProvider | null>(null)
  const [contracts, setContracts] = useState<ProviderContractItem[]>([])

  const loadContracts = useCallback(() => {
    return CredentialingProvidersService.listContracts(providerId).then(setContracts).catch(() => {})
  }, [providerId])
  const reloadProvider = useCallback(() => {
    return CredentialingProvidersService.show(providerId).then(setProvider).catch(() => {})
  }, [providerId])

  const onContractUploaded = useCallback(async () => {
    await loadContracts(); onChanged?.()
  }, [loadContracts, onChanged])
  const onSpecialtyTestChanged = useCallback(async () => {
    await reloadProvider(); onChanged?.()
  }, [reloadProvider, onChanged])

  useEffect(() => {
    let active = true
    setLoading(true)
    const tasks: Promise<unknown>[] = [
      CredentialingProvidersService.show(providerId).then(p => { if (active) setProvider(p) }),
    ]
    if (mode === 'contract') tasks.push(loadContracts())
    Promise.all(tasks).finally(() => active && setLoading(false))
    return () => { active = false }
  }, [providerId, mode, loadContracts])

  const title = {
    specialties: 'Especialidades',
    tests: 'Exames',
    bank: 'Dados bancários',
    contract: 'Contrato',
  }[mode]

  // Especialidades/Exames usam DataTable → modal mais larga para não espremer.
  const size = (mode === 'specialties' || mode === 'tests') ? 'xl' : 'md'

  return (
    <Modal open onClose={onClose} size={size} title={`${title} — ${providerName}`} footer={<Button variant="ghost" onClick={onClose}>Fechar</Button>}>
      {loading ? (
        <div className="flex items-center justify-center py-10"><Loader2 size={24} className="animate-spin text-gray-400" /></div>
      ) : (
        <div className="text-sm">
          {mode === 'specialties' && (
            <SpecialtyTestTable
              kind="specialty"
              providerId={providerId}
              rows={(provider?.specialties ?? []).map(s => ({
                pivotId: s.pivotId, name: s.specialty, formatted: s.formatted, telemedicine: s.telemedicine,
              }))}
              onChanged={onSpecialtyTestChanged}
            />
          )}
          {mode === 'tests' && (
            <SpecialtyTestTable
              kind="test"
              providerId={providerId}
              rows={(provider?.tests ?? []).map(t => ({
                pivotId: t.pivotId, name: t.test, formatted: t.formatted,
              }))}
              onChanged={onSpecialtyTestChanged}
            />
          )}
          {mode === 'bank' && <BankView provider={provider} />}
          {mode === 'contract' && <ContractView providerId={providerId} contracts={contracts} onUploaded={onContractUploaded} />}
        </div>
      )}
    </Modal>
  )
}

function EmptyRow({ children }: { children: React.ReactNode }) {
  return <div className="rounded-md border border-dashed border-gray-300 p-6 text-center text-gray-400">{children}</div>
}

function BankView({ provider }: { provider: CredentialingProvider | null }) {
  const b = provider?.bankDetails
  if (!b) return <EmptyRow>Dados bancários não cadastrados.</EmptyRow>
  const rows: Array<[string, string | null | undefined]> = [
    ['Tipo de transação', b.transaction],
    ['Beneficiário', b.beneficiary],
    ['Chave PIX', b.pixKey],
    ['Banco', b.bank ? `${b.bank.code} — ${b.bank.name}` : null],
    ['Agência', b.agency],
    ['Conta', b.account],
    ['Tipo de conta', b.accountType],
    ['Titular (PF/PJ)', b.type],
    ['Documento', b.document],
  ]
  return (
    <dl className="divide-y divide-gray-100">
      {rows.filter(([, v]) => v).map(([k, v]) => (
        <div key={k} className="flex items-center justify-between py-2">
          <dt className="text-gray-500">{k}</dt>
          <dd className="text-gray-800 font-medium">{v}</dd>
        </div>
      ))}
    </dl>
  )
}

function ContractView({ providerId, contracts, onUploaded }: {
  providerId: string
  contracts: ProviderContractItem[]
  onUploaded: () => void | Promise<void>
}) {
  const { can } = useAuthority()
  const canManageContract = can('credentialing.providers.manage_contract')
  const latest = contracts[0] ?? null
  return (
    <div className="space-y-4">
      {latest ? (
        <div className="flex items-center justify-between gap-3 rounded-md ring-1 ring-gray-200 bg-gray-50 px-3 py-2.5">
          <span className="flex items-center gap-2 text-sm text-gray-700 min-w-0">
            <FileText size={16} className="text-gray-400 shrink-0" />
            <span className="break-words">{latest.fileName ?? 'Contrato.pdf'}</span>
            {latest.contractSignedAt && <span className="text-xs text-gray-400 shrink-0">· {dateBR(latest.contractSignedAt)}</span>}
          </span>
          {latest.contractUrl && (
            <a href={latest.contractUrl} target="_blank" rel="noreferrer"
              className="cursor-pointer inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-brand-600 hover:bg-brand-50">
              <FileText size={12} /> Ver
            </a>
          )}
        </div>
      ) : (
        <EmptyRow>Nenhum contrato anexado.</EmptyRow>
      )}

      {canManageContract && (
      <div>
        <label className="text-xs font-medium text-gray-700 mb-1 block">
          {latest ? 'Alterar contrato' : 'Anexar contrato'}
        </label>
        <FileUpload
          accept="pdf"
          currentUrl={null}
          currentName={null}
          maxMB={20}
          hint="PDF até 20MB."
          onSelect={async (file) => {
            try {
              await CredentialingProvidersService.uploadContract(providerId, file)
              toast.success({ title: 'Contrato enviado', description: 'O PDF foi anexado.' })
              await onUploaded()
            } catch {
              toast.error({ title: 'Falha no upload', description: 'Não foi possível enviar o contrato.' })
            }
          }}
        />
      </div>
      )}

      {contracts.length > 1 && (
        <div>
          <div className="text-xs font-medium text-gray-500 mb-1">Histórico</div>
          <ul className="divide-y divide-gray-100">
            {contracts.slice(1).map(c => (
              <li key={c.id} className="flex items-center justify-between py-2 text-sm">
                <span className="flex items-center gap-2 text-gray-600 min-w-0">
                  <FileText size={13} className="text-gray-300 shrink-0" />
                  <span className="break-words">{c.fileName ?? 'Contrato.pdf'}</span>
                </span>
                {c.contractUrl && <a href={c.contractUrl} target="_blank" rel="noreferrer" className="text-xs text-brand-600 hover:underline">Ver</a>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
